import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  restoreUser,
  assignRoleToUser,
  removeRoleFromUser,
  uploadUserAvatar,
} from "../../../services/UserService";
import { getRoles } from "../../../services/RoleService";
import { roleName, roleDescription } from "../../../utils/accessLabels";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";
import { getLocale } from "../../../utils/i18nLabels";

const emptyForm = {
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  phone: "",
  is_active: true,
  role_ids: [],
  // Identité de base ("Matricule, nom, prénom, sexe, date de naissance"),
  // commune à tout utilisateur, quel que soit son rôle.
  nni: "",
  gender: "",
  date_of_birth: "",
  place_of_birth: "",
  nationality: "",
};

const Users = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.USER_CREATE);
  const canUpdate = can(PERMISSIONS.USER_UPDATE);
  const canDelete = can(PERMISSIONS.USER_DELETE);
  // Data
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  // Catalogue des rôles (pour les cases à cocher / le filtre)
  const [allRoles, setAllRoles] = useState([]);

  // Pagination (server-side)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const limitOptions = [5, 10, 15, 20, 25, 50, 100];

  // Filtres / tri
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Modal Ajout / Modification
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [initialRoleIds, setInitialRoleIds] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  // Photo de profil (modal d'édition uniquement, l'utilisateur doit déjà exister)
  const [editingAvatarUrl, setEditingAvatarUrl] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const avatarInputRef = useRef(null);

  // Modal suppression / restauration
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bandeau de retour
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchRolesCatalog = useCallback(async () => {
    try {
      const res = await getRoles({ page: 1, limit: 100, sort_by: "name", sort_dir: "asc" });
      setAllRoles(res.data?.data || []);
    } catch (err) {
      // le filtre/les cases à cocher resteront vides, réessayable via "Actualiser"
    }
  }, []);

  useEffect(() => {
    fetchRolesCatalog();
  }, [fetchRolesCatalog]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (isActiveFilter !== "") params.is_active = isActiveFilter;
      if (showDeleted) params.with_trashed = true;

      const res = await getUsers(params);
      const body = res.data;
      setUsers(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
        t("pages.users.impossibleChargerListeUtilisateurs"),
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortDir, search, roleFilter, isActiveFilter, showDeleted, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const getSortIcon = (field) => (sortBy !== field ? "↕" : sortDir === "asc" ? "↑" : "↓");

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setRoleFilter("");
    setIsActiveFilter("");
    setShowDeleted(false);
    setSortBy("created_at");
    setSortDir("desc");
    setPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(value);
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setInitialRoleIds([]);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setEditingAvatarUrl("");
    setAvatarError("");
    setShowFormModal(true);
  };

  const openEditModal = (user) => {
    setFormMode("edit");
    const roleIds = (user.roles || []).map((r) => r.id);
    setFormData({
      email: user.email || "",
      password: "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      phone: user.phone || "",
      is_active: !!user.is_active,
      role_ids: roleIds,
      nni: user.nni || "",
      gender: user.gender || "",
      date_of_birth: user.date_of_birth ? user.date_of_birth.substring(0, 10) : "",
      place_of_birth: user.place_of_birth || "",
      nationality: user.nationality || "",
    });
    setInitialRoleIds(roleIds);
    setFormErrors({});
    setSubmitError("");
    setEditingId(user.id);
    setEditingAvatarUrl(user.avatar_url || "");
    setAvatarError("");
    setShowFormModal(true);
  };

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editingId) return;

    setAvatarError("");
    setAvatarUploading(true);
    try {
      const res = await uploadUserAvatar(editingId, file);
      const updated = res.data?.data;
      setEditingAvatarUrl(updated?.avatar_url || "");
      // Reflète immédiatement le changement dans la liste, sans recharger toute la page
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingId ? { ...u, avatar: updated?.avatar, avatar_url: updated?.avatar_url } : u,
        ),
      );
    } catch (err) {
      setAvatarError(
        err?.response?.data?.message || t("header.avatar.error"),
      );
    } finally {
      setAvatarUploading(false);
    }
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleRole = (roleId) => {
    setFormData((prev) => {
      const has = prev.role_ids.includes(roleId);
      return {
        ...prev,
        role_ids: has
          ? prev.role_ids.filter((id) => id !== roleId)
          : [...prev.role_ids, roleId],
      };
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email.trim()) errors.email = t("common.ui.lEmailEstRequis");
    if (formMode === "add" && !formData.password.trim())
      errors.password = t("pages.users.passwordRequired");
    if (formData.password && formData.password.length < 6)
      errors.password = t("pages.users.passwordMinLength");
    if (formData.role_ids.length === 0)
      errors.role_ids = t("pages.users.rolesRequired");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      if (formMode === "add") {
        const payload = {
          email: formData.email.trim(),
          password: formData.password,
          first_name: formData.first_name.trim() || null,
          last_name: formData.last_name.trim() || null,
          phone: formData.phone.trim() || null,
          is_active: formData.is_active,
          role_ids: formData.role_ids,
          nni: formData.nni.trim() || null,
          gender: formData.gender || null,
          date_of_birth: formData.date_of_birth || null,
          place_of_birth: formData.place_of_birth.trim() || null,
          nationality: formData.nationality.trim() || null,
        };
        await createUser(payload);
        setFeedback({ type: "success", message: t("pages.users.createSuccess") });
      } else {
        const payload = {
          email: formData.email.trim(),
          first_name: formData.first_name.trim() || null,
          last_name: formData.last_name.trim() || null,
          phone: formData.phone.trim() || null,
          is_active: formData.is_active,
          nni: formData.nni.trim() || null,
          gender: formData.gender || null,
          date_of_birth: formData.date_of_birth || null,
          place_of_birth: formData.place_of_birth.trim() || null,
          nationality: formData.nationality.trim() || null,
        };
        if (formData.password.trim()) payload.password = formData.password;

        await updateUser(editingId, payload);

        // Synchronise les rôles (diff avec l'état initial)
        const added = formData.role_ids.filter((id) => !initialRoleIds.includes(id));
        const removed = initialRoleIds.filter((id) => !formData.role_ids.includes(id));

        // eslint-disable-next-line no-restricted-syntax
        for (const roleId of added) {
          // eslint-disable-next-line no-await-in-loop
          await assignRoleToUser(editingId, roleId);
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const roleId of removed) {
          // eslint-disable-next-line no-await-in-loop
          await removeRoleFromUser(editingId, roleId);
        }

        setFeedback({ type: "success", message: t("pages.users.updateSuccess") });
      }

      setShowFormModal(false);
      fetchUsers();
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ||
        t("common.crud.saveError"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteUser(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.users.deactivateSuccess") });
      setDeleteTarget(null);
      if (users.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        fetchUsers();
      }
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message ||
          t("pages.users.deactivateError"),
      });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (user) => {
    try {
      await restoreUser(user.id);
      setFeedback({ type: "success", message: t("pages.users.restoreSuccess") });
      fetchUsers();
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message || t("pages.users.restoreError"),
      });
    }
  };

  const toggleActive = async (user) => {
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      setUsers((prev) =>
        prev.map((item) =>
          item.id === user.id ? { ...item, is_active: !user.is_active } : item,
        ),
      );
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message || t("common.crud.toggleError"),
      });
    }
  };

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* En-tête */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("pages.users.breadcrumb")}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button
                type="button"
                className="btn btn-outline-light bg-white btn-icon me-1"
                title={t("common.crud.refresh")}
                onClick={resetFilters}
              >
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            {canCreate && (
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center"
                  onClick={openAddModal}
                >
                  <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.users.addLabel")}
                </button>
              </div>
            )}
          </div>
        </div>

        {feedback && (
          <div
            className={`alert alert-${feedback.type} alert-dismissible fade show`}
            role="alert"
          >
            {feedback.message}
            <button
              type="button"
              className="btn-close"
              onClick={() => setFeedback(null)}
            ></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.users.title")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <span className="icon-addon"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.users.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ paddingLeft: "35px", minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.fields.allRoles")}</option>
                  {allRoles.map((role) => (
                    <option key={role.id} value={role.code}>
                      {roleName(t, role)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={isActiveFilter}
                  onChange={(e) => {
                    setIsActiveFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.crud.allStatuses")}</option>
                  <option value="true">{t("common.crud.filterActive")}</option>
                  <option value="false">{t("common.crud.filterInactive")}</option>
                </select>
              </div>
              <div className="mb-3 form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="show-deleted-users"
                  checked={showDeleted}
                  onChange={(e) => {
                    setShowDeleted(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label" htmlFor="show-deleted-users">
                  {t("pages.users.includeDeleted")}
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center justify-content-between px-3 py-2 border-bottom">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-600">
                <Trans i18nKey="common.pagination.showingEntries" values={{ start, end, total }} components={[<span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />]} />
              </span>
              <span className="text-gray-300">|</span>
              <div className="d-flex align-items-center gap-2">
                <span className="text-sm text-gray-600">
                  {t("common.pagination.rowsPerPage")}
                </span>
                <Dropdown className="drop-select">
                  <Dropdown.Toggle
                    as="div"
                    className="drop-select-btn"
                    style={{
                      minWidth: "70px",
                      background: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      padding: "4px 12px",
                      cursor: "pointer",
                    }}
                  >
                    {limit}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {limitOptions.map((option) => (
                      <Dropdown.Item
                        key={option}
                        onClick={() => handleLimitChange(option)}
                        className={limit === option ? "active" : ""}
                      >
                        {option}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
                      {t("common.fields.email")} {getSortIcon("email")}
                    </th>
                    <th>{t("common.fields.name")}</th>
                    <th>{t("common.fields.roles")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th onClick={() => handleSort("created_at")} style={{ cursor: "pointer" }}>
                      {t("common.fields.createdAt")} {getSortIcon("created_at")}
                    </th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="text-danger">{loadError}</div>
                      </td>
                    </tr>
                  ) : users.length > 0 ? (
                    users.map((user) => {
                      const name =
                        [user.first_name, user.last_name].filter(Boolean).join(" ") || "-";
                      const initials = (
                        [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email
                      )
                        .slice(0, 2)
                        .toUpperCase();
                      return (
                        <tr key={user.id}>
                          <td>
                            <span className="fw-medium">{user.email}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center tw-gap-2">
                              <span className="avatar avatar-sm flex-shrink-0 bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center">
                                {user.avatar_url ? (
                                  <img src={user.avatar_url} className="img-fluid rounded-circle" alt={name} />
                                ) : (
                                  <span className="text-primary fw-semibold fs-12">{initials}</span>
                                )}
                              </span>
                              <span>{name}</span>
                            </div>
                          </td>
                          <td>
                            {(user.roles || []).length > 0 ? (
                              user.roles.map((role) => (
                                <span
                                  key={role.id}
                                  className="badge badge-soft-primary me-1"
                                >
                                  {roleName(t, role)}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`badge badge-soft-${user.is_active ? "success" : "danger"} d-inline-flex align-items-center`}
                              role="button"
                              onClick={() => toggleActive(user)}
                              title={t("common.crud.toggleStatusHint")}
                            >
                              <i className="ti ti-circle-filled fs-5 me-1"></i>
                              {user.is_active ? t("common.crud.statusActive") : t("common.crud.statusInactive")}
                            </span>
                          </td>
                          <td>
                            {user.created_at
                              ? new Date(user.created_at).toLocaleDateString(getLocale())
                              : "-"}
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle
                                as="button"
                                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                              >
                                <i className="ti ti-dots-vertical fs-14"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end" className="p-3">
                                {canUpdate && (
                                  <Dropdown.Item
                                    className="rounded-1"
                                    onClick={() => openEditModal(user)}
                                  >
                                    <i className="ti ti-edit-circle me-2"></i>
                                    {t("pages.users.editRoles")}
                                  </Dropdown.Item>
                                )}
                                <Dropdown.Item
                                  className="rounded-1"
                                  onClick={() => toggleActive(user)}
                                >
                                  <i className="ti ti-toggle-right me-2"></i>
                                  {user.is_active ? t("common.crud.deactivate") : t("common.crud.activate")}
                                </Dropdown.Item>
                                {user.deleted_at ? (
                                  <Dropdown.Item
                                    className="rounded-1"
                                    onClick={() => handleRestore(user)}
                                  >
                                    <i className="ti ti-rotate me-2"></i>
                                    {t("pages.users.restore")}
                                  </Dropdown.Item>
                                ) : (
                                  canDelete && (
                                    <Dropdown.Item
                                      className="rounded-1"
                                      onClick={() => setDeleteTarget(user)}
                                    >
                                      <i className="ti ti-trash-x me-2"></i>
                                      {t("common.crud.delete")}
                                    </Dropdown.Item>
                                  )
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="text-center">
                          <i className="ti ti-search-off fs-1 text-muted"></i>
                          <h5 className="mt-2">{t("pages.users.aucunUtilisateurTrouve")}</h5>
                          <p className="text-muted">
                            {t("common.ui.essayezModifierVosCriteresRecherche")}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-between px-3 pt-3 border-top">
              <div className="text-sm text-gray-600">
                <Trans i18nKey="common.pagination.showingEntries" values={{ start, end, total }} components={[<span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />]} />
              </div>

              {total > 0 && (
                <div className="flex items-center gap-1">
                  <button
                    className={`px-3 py-1 rounded-md border ${page === 1 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    disabled={page === 1}
                  >
                    <i className="fa-solid fa-angle-left"></i>
                  </button>
                  {[...Array(totalPages).keys()].map((n) => {
                    const pageNum = n + 1;
                    const totalPagesToShow = 5;
                    const halfWay = Math.ceil(totalPagesToShow / 2);
                    const startPage = Math.max(
                      1,
                      Math.min(page - halfWay + 1, totalPages - totalPagesToShow + 1),
                    );
                    const endPage = Math.min(totalPages, startPage + totalPagesToShow - 1);

                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= startPage && pageNum <= endPage)
                    ) {
                      return (
                        <button
                          key={n}
                          className={`px-3 py-1 rounded-md border ${page === pageNum ? "bg-primary text-white" : "text-gray-700 hover:bg-gray-100"}`}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === startPage - 1 || pageNum === endPage + 1) {
                      return (
                        <span key={n} className="px-2 text-gray-400">
                          …
                        </span>
                      );
                    }
                    return null;
                  })}
                  <button
                    className={`px-3 py-1 rounded-md border ${page === totalPages ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={page === totalPages}
                  >
                    <i className="fa-solid fa-angle-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Ajout / Modification */}
      <Modal show={showFormModal} onHide={closeFormModal} centered size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {formMode === "add" ? t("pages.users.addLabel") : t("pages.users.editTitle")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && (
              <div className="alert alert-danger">{submitError}</div>
            )}
            {formMode === "edit" && (
              <div className="d-flex align-items-center tw-gap-3 mb-3 pb-3 border-bottom">
                <div className="position-relative">
                  <span
                    className="avatar avatar-lg flex-shrink-0 bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center"
                    style={{ opacity: avatarUploading ? 0.5 : 1 }}
                  >
                    {editingAvatarUrl ? (
                      <img src={editingAvatarUrl} className="img-fluid rounded-circle" alt="" />
                    ) : (
                      <i className="ti ti-user fs-24 text-primary"></i>
                    )}
                  </span>
                  <button
                    type="button"
                    title={t("header.avatar.changePhoto")}
                    onClick={handleAvatarClick}
                    disabled={avatarUploading}
                    className="tw-absolute tw-bottom-0 tw-right-0 tw-w-4 tw-h-4 tw-rounded-full tw-bg-primary tw-border tw-border-white tw-flex tw-items-center tw-justify-center tw-p-0"
                    style={{ fontSize: 8, lineHeight: 1 }}
                  >
                    <i className="ti ti-camera text-white"></i>
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="d-none"
                    onChange={handleAvatarChange}
                  />
                </div>
                <div>
                  <p className="mb-0 fw-medium">{t("pages.users.profilePhoto")}</p>
                  <p className="mb-0 text-muted small">
                    {avatarUploading ? t("pages.users.avatarUploading") : t("pages.users.avatarChangeHint")}
                  </p>
                  {avatarError && <p className="mb-0 text-danger small">{avatarError}</p>}
                </div>
              </div>
            )}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.fields.email")} <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className={`form-control ${formErrors.email ? "is-invalid" : ""}`}
                  placeholder="prof.dupont@example.com"
                  value={formData.email}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                />
                {formErrors.email && (
                  <div className="invalid-feedback">{formErrors.email}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.fields.password")}
                  {formMode === "edit" && (
                    <span className="text-muted fw-normal">
                      {t("pages.users.passwordKeepHint")}
                    </span>
                  )}
                  {formMode === "add" && <span className="text-danger">*</span>}
                </label>
                <input
                  type="password"
                  className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                  placeholder="••••••"
                  value={formData.password}
                  onChange={(e) => handleFormChange("password", e.target.value)}
                />
                {formErrors.password && (
                  <div className="invalid-feedback">{formErrors.password}</div>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.fields.firstName")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.first_name}
                  onChange={(e) => handleFormChange("first_name", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.fields.name")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.last_name}
                  onChange={(e) => handleFormChange("last_name", e.target.value)}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("common.fields.phone")}</label>
              <input
                type="text"
                className="form-control"
                value={formData.phone}
                onChange={(e) => handleFormChange("phone", e.target.value)}
              />
            </div>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("common.ui.sexe")}</label>
                <select
                  className="form-select"
                  value={formData.gender}
                  onChange={(e) => handleFormChange("gender", e.target.value)}
                >
                  <option value="">-</option>
                  <option value="M">{t("common.ui.masculin")}</option>
                  <option value="F">{t("common.ui.feminin")}</option>
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("common.ui.dateNaissance")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.date_of_birth}
                  onChange={(e) => handleFormChange("date_of_birth", e.target.value)}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("common.ui.lieuNaissance")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.place_of_birth}
                  onChange={(e) => handleFormChange("place_of_birth", e.target.value)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.ui.nniNumeroNationalIdentite")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.nni}
                  onChange={(e) => handleFormChange("nni", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.ui.nationalite")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("common.ui.exMauritanienne")}
                  value={formData.nationality}
                  onChange={(e) => handleFormChange("nationality", e.target.value)}
                />
              </div>
            </div>
            <div className="form-check form-switch mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="user-is-active"
                checked={formData.is_active}
                onChange={(e) => handleFormChange("is_active", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="user-is-active">
                {t("pages.users.activeAccount")}
              </label>
            </div>

            <hr />
            <label className="form-label fw-bold">
              {t("common.fields.roles")} <span className="text-danger">*</span>
            </label>
            {formErrors.role_ids && (
              <div className="text-danger small mb-2">{formErrors.role_ids}</div>
            )}
            {allRoles.length === 0 ? (
              <div className="text-muted">{t("pages.users.noRolesAvailable")}</div>
            ) : (
              <div className="row">
                {allRoles.map((role) => (
                  <div className="col-md-6" key={role.id}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`user-role-${role.id}`}
                        checked={formData.role_ids.includes(role.id)}
                        onChange={() => toggleRole(role.id)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`user-role-${role.id}`}
                        title={roleDescription(t, role) || role.code}
                      >
                        {roleName(t, role)}{" "}
                        <span className="text-muted">({role.code})</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-light"
              onClick={closeFormModal}
              disabled={submitting}
            >
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting
                ? t("common.crud.saving")
                : formMode === "add"
                  ? t("common.crud.add")
                  : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Modal Suppression */}
      <Modal
        show={deleteTarget !== null}
        onHide={() => (deleting ? null : setDeleteTarget(null))}
        centered
      >
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("pages.users.confirmDeactivateTitle")}</h4>
          <p>
            <Trans i18nKey="pages.users.voulezVousVraimentDesactiverUtilisateurIl" values={{ email: deleteTarget?.email }} components={[<strong />]} />
          </p>
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-light me-3"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              {t("common.crud.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? t("pages.users.deactivating") : t("pages.users.confirmDeactivate")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Users;